import {
  CardPaymentEventTypes,
  CardPaymentSelectors,
  CartQuoteError,
  CartQuoteErrorModal,
  CartQuoteEventType,
  CartQuoteState,
  CartState,
  ContactEventType,
  ParentContextWithWallet,
  ParentState,
  PaymentInformationChildState,
  PaymentInformationState,
  RewardsPaymentEventTypes,
  RewardsPaymentSelectors,
  WalletActions,
  WalletEventTypes,
  WalletSelectors,
  WalletServices,
  combineServices,
  getCartFulfillMachine,
  getCartQuoteMachine,
  getCartUpdateMachine,
  getContactInformationMachine,
  getReviewMachineState,
  trackPollFailure,
  trackPollSuccess,
  trackQuotePollFailure,
  trackQuotePollSuccess,
} from "@capone/checkout";
import { DoneInvokeEvent, MachineConfig } from "xstate";

import { COMPLETE_BUY_ACTIVITY, PRICE_QUOTE_ACTIVITY } from "redmond";
import { QuoteFailure, QuoteSuccess } from "@b2bportal/purchase-api";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  getGlobalTrackingProperties,
  getConfigTenant,
} from "@capone/common";
import dayjs from "dayjs";
import { store } from "b2b-base/src/app/store";
import { ActionTypes } from "./actions";
import { Event, TEvent } from "./events";
import { getInitialActivityMachineContext } from "./initialContext";
import { Services } from "./services";
import { ActivityMachineContext } from "./types";
import { GuardTypes } from "./guards";
import {
  getActivityTravelerMachine,
  getActivityAdditionalDetailsMachine,
  getCardPaymentMachine,
} from "../../../checkout";
import {
  TravelerInformationChildState,
  TravelerInformationState,
} from "../../../checkout/states/TravelerInformation/types";
import { getAdditionalEncryptedProperties } from "../../../utils/encryptedPropertiesUtils";
import { buildActivityBookingData } from "../utils/buildActivityBookingData";

export const activityMachineId = "capone-activity-checkout";

export const activityBookStateMachine: MachineConfig<
  ActivityMachineContext,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  TEvent
> = {
  schema: {
    context: getInitialActivityMachineContext(),
    events: {} as TEvent,
  },

  // Machine identifier
  id: activityMachineId,

  // Initial state
  initial: ParentState.loading,

  // State definitions
  states: {
    [ParentState.loading]: {
      type: `parallel`,
      states: {
        fetchContactInfo: {
          initial: ParentState.loading,
          states: {
            [ParentState.loading]: {
              invoke: {
                src: Services.fetchContactInfoService,
                onDone: {
                  actions: ActionTypes.setContactInfo,
                  target: `loaded`,
                },
                onError: {},
              },
            },
            loaded: {
              type: "final",
            },
          },
        },
        fetchTravelWalletItems: {
          initial: ParentState.loading,
          states: {
            [ParentState.loading]: {
              invoke: {
                src: (context: ParentContextWithWallet) =>
                  combineServices([
                    WalletServices.getWalletOffers(context),
                    WalletServices.getWalletCredits(context),
                    WalletServices.getWalletCreditHistory(context),
                  ]),
                onDone: {
                  actions: [
                    WalletActions.handleGetWalletOffers,
                    WalletActions.setCreditAmountToApply,
                  ],
                  target: `loaded`,
                },
                onError: {
                  actions: WalletActions.setWalletError,
                },
              },
            },
            loaded: {
              type: "final",
            },
          },
        },
      },
      onDone: {
        target: `#${activityMachineId}.${ParentState.activityTravelerInformation}`,
      },
    },
    [ParentState.activityTravelerInformation]: {
      ...getActivityTravelerMachine({
        parentMachineId: activityMachineId,
        nextState: `#${activityMachineId}.${ParentState.activityPrimaryTraveler}`,
        nextStateFromReview: `#${activityMachineId}.${ParentState.activityPrimaryTraveler}`,
      }),
    },
    [ParentState.contactInformation]: {
      ...getContactInformationMachine({
        parentMachineId: activityMachineId,
        previousState: `#${activityMachineId}.${ParentState.activityTravelerInformation}`,
        onDone: [
          {
            target: `#${activityMachineId}.${ParentState.activityPrimaryTraveler}`,
          },
        ],
        onError: [
          {
            target: `#${activityMachineId}.${ParentState.activityTravelerInformation}`,
          },
        ],
      }),
    },
    [ParentState.activityPrimaryTraveler]: {
      initial: TravelerInformationState.travelerPicker,
      entry: ActionTypes.setPrimaryTravelerToSingleSelected,
      always: [
        {
          target: `#${activityMachineId}.${ParentState.activityAdditionalDetails}`,
          cond: GuardTypes.isSingleSelectedTravelerWithAdditionalDetails,
        },
        {
          target: `#${activityMachineId}.${ParentState.cartQuote}`,
          cond: GuardTypes.isSingleSelectedTraveler,
        },
      ],
      states: {
        [TravelerInformationState.travelerPicker]: {
          initial: TravelerInformationChildState.idle,
          states: {
            [TravelerInformationChildState.idle]: {},
          },
        },
        [TravelerInformationState.error]: {},
      },
      on: {
        [Event.NEXT]: [
          {
            target: `#${activityMachineId}.${ParentState.activityAdditionalDetails}`,
            cond: GuardTypes.hasAdditionalDetailsStep,
          },
          {
            target: `#${activityMachineId}.${ParentState.cartQuote}`,
          },
        ],
        [Event.PREVIOUS]: [
          {
            target: `#${activityMachineId}.${ParentState.activityTravelerInformation}`,
          },
        ],
        [Event.SELECT_TRAVELER]: [
          {
            actions: ActionTypes.selectPrimaryTraveler,
          },
        ],
      },
    },
    [ParentState.activityAdditionalDetails]: {
      ...getActivityAdditionalDetailsMachine({
        parentMachineId: activityMachineId,
        nextState: `#${activityMachineId}.${ParentState.cartQuote}`,
        nextStateFromReview: `#${activityMachineId}.${ParentState.cartQuote}`,
        previousState: `#${activityMachineId}.${ParentState.activityPrimaryTraveler}`,
      }),
    },
    [ParentState.cartQuote]: {
      entry: ActionTypes.addActivityProduct,
      ...getCartQuoteMachine({
        parentMachineId: activityMachineId,
        nextState: `#${activityMachineId}.${ParentState.cardPayment}`,
        trackPollSuccessAction: trackQuotePollSuccess({
          eventName: PRICE_QUOTE_ACTIVITY,
          getAdditionalProperties: (context: ActivityMachineContext) => {
            const {
              shopOptionTrackingProperties,
              activityShop,
              activityDetailsTrackingProperties,
            } = context;
            return {
              ...(shopOptionTrackingProperties?.properties || {}),
              ...(activityShop?.trackingProperties?.properties || {}),
              // Last: already includes the two above, plus the frontend-corrected values.
              ...(activityDetailsTrackingProperties?.properties || {}),
              total_rewards_amount_usd:
                RewardsPaymentSelectors.getRewardsFiatAmountToApply({
                  context,
                })?.value || 0,
              total_card_amount_usd:
                CardPaymentSelectors.getTotalCardPaymentRequired({
                  context,
                }),
              credit_amt_redeemed: Math.abs(
                WalletSelectors.getTravelWalletCreditToApply({ context })
                  ?.amount.amount || 0,
              ),
              total_offers_amount_usd: Math.abs(
                WalletSelectors.getSelectedOffer({ context })?.amount.amount ||
                  0,
              ),
              card_product_used:
                CardPaymentSelectors.getSelectedPaymentCardType({
                  context,
                }),
              product: getGlobalTrackingProperties(
                getConfigTenant(),
                store.getState().rootReducer,
              ).product,
            };
          },
          getAdditionalEncryptedProperties,
        }),
        trackPollFailureAction: trackQuotePollFailure({
          eventName: PRICE_QUOTE_ACTIVITY,

          getAdditionalProperties: (
            context: ActivityMachineContext,
            event: DoneInvokeEvent<
              | QuoteSuccess
              | {
                  data: Array<QuoteFailure>;
                  type: CartQuoteErrorModal;
                }
            >,
          ) => {
            const {
              shopOptionTrackingProperties,
              activityShop,
              activityDetailsTrackingProperties,
            } = context;

            return {
              ...(shopOptionTrackingProperties?.properties || {}),
              ...(activityShop?.trackingProperties?.properties || {}),
              // Last: already includes the two above, plus the frontend-corrected values.
              ...(activityDetailsTrackingProperties?.properties || {}),
              type: (event.data as CartQuoteError).type,
            };
          },
        }),
      }),
      exit: [ActionTypes.checkForPriceChange],
    },
    [ParentState.cardPayment]: {
      ...getCardPaymentMachine({
        parentMachineId: activityMachineId,
        nextState: `#${activityMachineId}.${ParentState.cartUpdateBeforeFulfill}`,
        previousState: `#${activityMachineId}.${ParentState.activityPrimaryTraveler}`,
      }),
    },
    [ParentState.review]: {
      ...getReviewMachineState({
        parentMachineId: activityMachineId,
        nextState: `#${activityMachineId}.${ParentState.cartUpdateBeforeFulfill}`,
        previousState: `#${activityMachineId}.${ParentState.cardPayment}`,
      }),
    },

    // Update cart with Products and Discounts
    [ParentState.cartUpdateBeforeFulfill]: {
      ...getCartUpdateMachine({
        entry: [WalletActions.addFinalizedPaymentDiscounts],
        parentMachineId: activityMachineId,
        nextState: `#${activityMachineId}.${ParentState.cartFulfill}`,
      }),
    },
    [ParentState.cartFulfill]: {
      entry: [
        ActionTypes.setPaymentFulfillParams,
        ActionTypes.setPrepareDetailsFulfillParams,
      ],
      ...getCartFulfillMachine<ActivityMachineContext>({
        parentMachineId: activityMachineId,
        nextState: `#${activityMachineId}.${ParentState.bookingConfirmation}`,

        trackPollSuccessAction: trackPollSuccess({
          eventName: COMPLETE_BUY_ACTIVITY,
          // trackProperties returned in poll fulfill response will be also added
          getAdditionalProperties: (
            context: ActivityMachineContext,
            event,
          ) => {
            const {
              shopOptionTrackingProperties,
              activityShop,
              activityDetailsTrackingProperties,
            } = context;

            return {
              ...(shopOptionTrackingProperties?.properties || {}),
              ...(activityShop?.trackingProperties?.properties || {}),
              // Last: already includes the two above, plus the frontend-corrected values.
              ...(activityDetailsTrackingProperties?.properties || {}),
              total_rewards_amount_usd:
                RewardsPaymentSelectors.getRewardsFiatAmountToApply({
                  context,
                })?.value || 0,
              total_card_amount_usd:
                CardPaymentSelectors.getTotalCardPaymentRequired({
                  context,
                }),
              credit_amt_redeemed: Math.abs(
                WalletSelectors.getTravelWalletCreditToApply({ context })
                  ?.amount.amount || 0,
              ),
              total_offers_amount_usd: Math.abs(
                WalletSelectors.getSelectedOffer({ context })?.amount.amount ||
                  0,
              ),
              card_product_used:
                CardPaymentSelectors.getSelectedPaymentCardType({
                  context,
                }),
              product: getGlobalTrackingProperties(
                getConfigTenant(),
                store.getState().rootReducer,
              ).product,
              reservation_id:
                "FulfillResponse" in event.data &&
                event.data.FulfillResponse === "Success" &&
                event.data.products[0].value.customerReservationId,
              booking_duration: dayjs().diff(
                context.initialTime,
                "seconds",
                true,
              ),
            };
          },
          getAdditionalEncryptedProperties,
          onDone: (context, event) => {
            travelAppInterface.sendEvent({
              name: TravelAppInterfaceEventName.BOOKING_COMPLETE,
              funnel: TravelAppInterfaceFunnelType.ACTIVITIES,
              data: buildActivityBookingData({
                earnedRewards: context[CartState.cartFulfill]?.earnedRewards,
                priceBreakdown: context.priceBreakdown,
                travelerCounts: context.travelerCounts,
                activityShop: context.activityShop,
                reservationId:
                  "FulfillResponse" in event.data &&
                  event.data.FulfillResponse === "Success" &&
                  event.data.products[0]?.value?.customerReservationId,
              }),
            });
          },
        }),
        trackPollFailureAction: trackPollFailure({
          eventName: COMPLETE_BUY_ACTIVITY,
          // trackProperties returned in poll fulfill response will be also added
          getAdditionalProperties: (context: ActivityMachineContext) => {
            const {
              shopOptionTrackingProperties,
              activityShop,
              activityDetailsTrackingProperties,
            } = context;

            return {
              ...(shopOptionTrackingProperties?.properties || {}),
              ...(activityShop?.trackingProperties?.properties || {}),
              // Last: already includes the two above, plus the frontend-corrected values.
              ...(activityDetailsTrackingProperties?.properties || {}),
            };
          },
        }),
      }),
    },
    [ParentState.bookingConfirmation]: {},
  },
  on: {
    [Event.OPEN_TRAVELER_PICKER]: `#${activityMachineId}.${ParentState.activityTravelerInformation}`,
    // wallet credit Payment Events,
    [WalletEventTypes.SET_SELECTED_OFFER]: {
      actions: ActionTypes.addDiscounts,
    },
    [WalletEventTypes.REMOVE_SELECTED_OFFER]: {
      actions: ActionTypes.removeDiscounts,
    },
    [WalletEventTypes.SET_CREDIT_AMOUNT_TO_APPLY]: {
      actions: ActionTypes.setCreditAmountToApply,
    },
    // Rewards Payment Events,
    [RewardsPaymentEventTypes.SET_SELECTED_REWARDS_ACCOUNT_ID]: {
      actions: ActionTypes.setSelectedRewardsAccountId,
    },
    [RewardsPaymentEventTypes.SET_REWARDS_FIAT_AMOUNT_TO_PAY]: {
      actions: ActionTypes.setRewardsFiatAmountToPay,
    },
    [RewardsPaymentEventTypes.SET_REWARDS_AMOUNT_TO_PAY]: {
      actions: ActionTypes.setRewardsAmountToPay,
    },
    [RewardsPaymentEventTypes.SET_REWARDS_PAYMENT_VISITED]: {
      actions: ActionTypes.setRewardsPaymentVisited,
    },
    [RewardsPaymentEventTypes.SET_REWARDS_ACCOUNTS]: {
      actions: ActionTypes.setRewardsAccounts,
    },
    [RewardsPaymentEventTypes.SET_EARN_BY_REWARDS_ACCOUNT_ID]: {
      actions: ActionTypes.setEarnValueForAccount,
    },
    // Card Payment Events
    [CardPaymentEventTypes.SET_SELECTED_PAYMENT_METHOD_ID]: {
      actions: ActionTypes.setSelectedPaymentMethodId,
    },
    [CardPaymentEventTypes.DELETE_PAYMENT_METHOD]: `#${activityMachineId}.${ParentState.cardPayment}.${PaymentInformationState.delete}`,
    [CardPaymentEventTypes.OPEN_PAYMENT_FORM]: `#${activityMachineId}.${ParentState.cardPayment}.${PaymentInformationState.add}`,
    [CardPaymentEventTypes.CLOSE_PAYMENT_FORM]: `#${activityMachineId}.${ParentState.cardPayment}.${PaymentInformationState.idle}`,
    [CardPaymentEventTypes.VERIFY_PAYMENT_METHOD]: {
      actions: ActionTypes.setSpreedlyToken,
      target: `#${activityMachineId}.${ParentState.cardPayment}.${PaymentInformationState.add}.${PaymentInformationChildState.verify}`,
    },
    [CardPaymentEventTypes.ACKNOWLEDGE_PRICE_CHANGE]: {
      actions: ActionTypes.acknowledgePriceChange,
    },
    // Contact Information Events
    [ContactEventType.CHANGE]: {
      actions: ActionTypes.setContactInfo,
    },
    // Events below are used for browser navigation
    [Event.GO_TO_TRAVELER_SELECT]: `#${activityMachineId}.${ParentState.activityTravelerInformation}`,
    [Event.GO_TO_CONTACT_INFORMATION]: `#${activityMachineId}.${ParentState.contactInformation}`,
    [Event.GO_TO_PRIMARY_TRAVELER_SELECT]: `#${activityMachineId}.${ParentState.activityPrimaryTraveler}`,
    [Event.GO_TO_ADDITIONAL_DETAILS]: `#${activityMachineId}.${ParentState.activityAdditionalDetails}`,
    [Event.GO_TO_CART_QUOTE]: `#${activityMachineId}.${ParentState.cartQuote}`,
    [Event.GO_TO_REVIEW]: `#${activityMachineId}.${ParentState.review}`,
    [Event.GO_TO_CARD_PAYMENT]: `#${activityMachineId}.${ParentState.cardPayment}`,
    [Event.GO_TO_BOOKING_CONFIRMATION]: `#${activityMachineId}.${ParentState.bookingConfirmation}`,
    [Event.SET_PLATFORM]: {
      actions: ActionTypes.setPlatform,
    },
    [CartQuoteEventType.RETRY_QUOTE]: {
      target: `#${activityMachineId}.${CartState.cartQuote}.${CartQuoteState.route}`,
      actions: [ActionTypes.setQuoteRetries, ActionTypes.clearCartQuoteError],
    },
    [Event.SET_INITIAL_BOOK_TIME]: {
      actions: ActionTypes.setInitialBookTime,
    },
  },
};
